import { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

export default function RedeemInvite() {
  const API = process.env.REACT_APP_API_BASE;
  const navigate = useNavigate();
  
  const { code: routeCode } = useParams();
  const [code, setCode] = useState(routeCode || "");
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // --- Password rules ---
  const emailLocal = useMemo(() => (email || "").split("@")[0] || "", [email]);

  const rules = useMemo(() => {
    const p = password || "";
    return {
      length: p.length >= 8,                            // ≥ 8 chars
      upper: /[A-Z]/.test(p),                           // at least 1 uppercase
      lower: /[a-z]/.test(p),                           // at least 1 lowercase
      number: /\d/.test(p),                             // at least 1 digit
      special: /[^A-Za-z0-9]/.test(p),                  // at least 1 symbol
      nospace: !/\s/.test(p),                           // no whitespace
      notEmailLocal: emailLocal ? !p.toLowerCase().includes(emailLocal.toLowerCase()) : true
    };
  }, [password, emailLocal]);

  const allRulesPass = Object.values(rules).every(Boolean);
  const match = password && confirm && password === confirm;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");

    if (!allRulesPass) {
      setErr("Please meet all password requirements.");
      return;
    }
    if (!match) {
      setErr("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/dealer/auth/redeem-invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, email, fullName, password })
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      const data = await res.json();
      localStorage.setItem("dealerToken", data.token);
      navigate("/dealer/leads", { replace: true });
    } catch (e) {
      setErr("Invite redemption failed. Check the code, email domain, or expiry.");
    } finally {
      setLoading(false);
    }
  };

  const Rule = ({ ok, label }) => (
    <div className={`text-sm flex items-center gap-2 ${ok ? "text-green-700" : "text-gray-600"}`}>
      <span aria-hidden className={`inline-block w-2.5 h-2.5 rounded-full ${ok ? "bg-green-600" : "bg-gray-300"}`} />
      <span>{label}</span>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <form onSubmit={submit} className="w-full max-w-md bg-white p-6 rounded-xl border">
        <h1 className="text-2xl font-semibold">Redeem Dealer Invite</h1>

        <div className="mt-4">
          <label className="block text-sm text-gray-700 mb-1">Invite code</label>
          <input
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Paste your invite code"
            required
          />
        </div>

        <div className="mt-3">
          <label className="block text-sm text-gray-700 mb-1">Work email</label>
          <input
            type="email"
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@dealerdomain.com"
            required
          />
        </div>

        <div className="mt-3">
          <label className="block text-sm text-gray-700 mb-1">Full name</label>
          <input
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Jane Doe"
            required
          />
        </div>

        {/* Password + rules */}
        <div className="mt-3">
          <label className="block text-sm text-gray-700 mb-1">Password</label>
          <input
            type="password"
            className={`w-full border rounded-lg px-3 py-2 ${allRulesPass ? "border-gray-300" : "border-red-300"}`}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 chars, mixed"
            required
            autoComplete="new-password"
          />
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
            <Rule ok={rules.length} label="At least 8 characters" />
            <Rule ok={rules.upper} label="At least 1 uppercase" />
            <Rule ok={rules.lower} label="At least 1 lowercase" />
            <Rule ok={rules.number} label="At least 1 number" />
            <Rule ok={rules.special} label="At least 1 symbol" />
            <Rule ok={rules.nospace} label="No spaces" />
            <Rule ok={rules.notEmailLocal} label="Doesn’t contain your email name" />
          </div>
        </div>

        {/* Confirm */}
        <div className="mt-3">
          <label className="block text-sm text-gray-700 mb-1">Confirm password</label>
          <input
            type="password"
            className={`w-full border rounded-lg px-3 py-2 ${match || !confirm ? "border-gray-300" : "border-red-300"}`}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Re-enter your password"
            required
            autoComplete="new-password"
          />
          {!match && confirm && (
            <div className="text-sm text-red-600 mt-1">Passwords do not match.</div>
          )}
        </div>

        {err && <div className="text-red-600 text-sm mt-3">{err}</div>}

        <button
          type="submit"
          disabled={loading || !allRulesPass || !match}
          className="mt-4 w-full bg-blue-600 text-white rounded-lg px-4 py-2 hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? "Creating account…" : "Create account"}
        </button>

        <div className="mt-3 text-sm text-gray-600 text-center">
          Already have an account? <a href="/dealer/login" className="text-blue-600 underline">Sign in</a>
        </div>
      </form>
    </div>
  );
}
